import { useRef, useState } from 'react';
import { Building2, Plus, X, Upload, Trash2, Globe, Star, AlertTriangle, TrendingUp } from 'lucide-react';
import clsx from 'clsx';
import {
  useDeletePhoto,
  useProperties,
  useProperty,
  useUploadPhotos,
} from '../api/properties';
import PropertyForm from '../components/PropertyForm';
import BandejaPortal from '../components/BandejaPortal';
import ContratoYDocumentos from '../components/ContratoYDocumentos';
import { ESTADO_LABEL, ESTADO_STYLE, MEDIA_BASE, OPERACION_LABEL, TIPO_LABEL, type Property } from '../lib/types';
import { AyudaGuia } from '../components/Ayuda';
import { NOMBRE_ACCION, tiempo, useVisitasVivienda, type Accion } from '../api/web';

const eur = (n?: number) => (n ? `${n.toLocaleString('es-ES')} €` : '—');

export default function ViviendasView() {
  const { data: items, isLoading, isError, error, refetch } = useProperties();
  const [creating, setCreating] = useState(false);
  // `?ficha=<property_id>` (lo usa «Ver ficha» en Tu web) abre esa vivienda.
  const [openId, setOpenId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('ficha'),
  );

  const kpis = {
    total: items?.length ?? 0,
    disponibles: items?.filter((p) => p.estado === 'disponible').length ?? 0,
    alquiladas: items?.filter((p) => p.estado === 'alquilada').length ?? 0,
    enWeb: items?.filter((p) => p.publicada_web === 'si').length ?? 0,
  };

  // Lo que espera decisión sale arriba, en su propia bandeja, y no mezclado en
  // la rejilla: si se pierde entre las demás tarjetas, no se resuelve nunca.
  const enEspera = (p: Property) =>
    p.estado === 'pendiente_validacion' || Boolean(p.cambios_pendientes);
  const cartera = items?.filter((p) => !enEspera(p)) ?? [];

  return (
    <div className="pb-16 md:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-extrabold tracking-tight text-deep">Viviendas</h1>
          <AyudaGuia seccion="viviendas" />
        </div>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition hover:bg-deep"
        >
          <Plus size={16} /> Nueva vivienda
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Total" value={kpis.total} />
        <Kpi label="Disponibles" value={kpis.disponibles} />
        <Kpi label="Alquiladas" value={kpis.alquiladas} />
        <Kpi label="En la web" value={kpis.enWeb} />
      </div>

      {isLoading ? (
        <p className="mt-10 text-sm text-ink/50">Cargando…</p>
      ) : isError ? (
        /* Un fallo del servidor no puede parecer una lista vacía: si aquí
           saliera "aún no hay viviendas", pensarías que se han perdido. */
        <div className="mt-8 rounded-2xl border border-dashed border-red-300 bg-red-50 px-6 py-12 text-center">
          <AlertTriangle className="mx-auto text-red-500" size={28} strokeWidth={1.5} />
          <p className="mt-3 font-bold text-red-700">No se han podido cargar las viviendas</p>
          <p className="mt-1 text-sm text-red-600/80">
            Es un fallo del servidor, no es que no haya ninguna. {(error as Error)?.message}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-full bg-red-600 px-5 py-2 text-sm font-bold text-white transition hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      ) : !items?.length ? (
        <div className="mt-8 rounded-2xl border border-dashed border-deep/20 bg-surface px-6 py-14 text-center">
          <Building2 className="mx-auto text-brand" size={28} strokeWidth={1.5} />
          <p className="mt-3 text-sm text-ink/60">
            Aún no hay viviendas. Crea la primera con «Nueva vivienda».
          </p>
        </div>
      ) : (
        <>
          <BandejaPortal items={items} />
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cartera.map((p) => (
              <PropertyCard key={p.property_id} p={p} onOpen={() => setOpenId(p.property_id)} />
            ))}
          </div>
        </>
      )}

      {creating && (
        <Modal title="Nueva vivienda" onClose={() => setCreating(false)}>
          <PropertyForm
            onDone={(saved) => {
              setCreating(false);
              setOpenId(saved.property_id);
            }}
          />
        </Modal>
      )}
      {openId && <PropertyDetail id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl bg-surface border border-deep/10 px-4 py-3">
      <p className="text-2xl font-extrabold text-deep">{value}</p>
      <p className="text-[0.68rem] font-bold uppercase tracking-wider text-ink/45">{label}</p>
    </div>
  );
}

function PropertyCard({ p, onOpen }: { p: Property; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="group overflow-hidden rounded-2xl border border-deep/10 bg-surface text-left transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-deep/10"
    >
      <div className="relative aspect-[4/3] bg-soft">
        {p.cover ? (
          <img
            src={`${MEDIA_BASE}/${p.cover}`}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-ink/25">
            <Building2 size={32} strokeWidth={1.25} />
          </div>
        )}
        <span
          className={clsx(
            'absolute left-3 top-3 rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider',
            ESTADO_STYLE[p.estado],
          )}
        >
          {ESTADO_LABEL[p.estado] ?? p.estado}
        </span>
        <div className="absolute right-3 top-3 flex gap-1.5">
          {p.destacada && (
            <span className="rounded-full bg-white/90 p-1 text-amber-500"><Star size={13} /></span>
          )}
          {p.publicada_web === 'si' && (
            <span className="rounded-full bg-white/90 p-1 text-brand"><Globe size={13} /></span>
          )}
        </div>
      </div>
      <div className="p-4">
        <p className="text-[0.65rem] font-bold uppercase tracking-wider text-ink/40">
          {p.codigo} · {TIPO_LABEL[p.tipo] ?? p.tipo}
        </p>
        <p className="mt-0.5 truncate font-bold text-deep">{p.direccion}</p>
        <p className="text-xs text-ink/50">{p.zona || '—'}</p>
        <div className="mt-3 flex items-center justify-between border-t border-deep/10 pt-3 text-xs text-ink/60">
          <span>{p.habitaciones} hab · {p.m2} m²</span>
          <span className="font-bold text-brand">
            {p.operacion !== 'venta' ? `${eur(p.precio_alquiler)}/mes` : eur(p.precio_venta)}
          </span>
        </div>
      </div>
    </button>
  );
}

function PropertyDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: p } = useProperty(id);
  const upload = useUploadPhotos(id);
  const removePhoto = useDeletePhoto(id);
  const fileRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);

  if (!p) return null;
  const pending = p.media?.filter((m) => m.status === 'uploading').length ?? 0;

  return (
    <Modal title={`${p.codigo} · ${p.direccion}`} onClose={onClose} wide>
      {editing ? (
        <PropertyForm initial={p} onDone={() => setEditing(false)} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className={clsx('rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider', ESTADO_STYLE[p.estado])}>
              {ESTADO_LABEL[p.estado] ?? p.estado}
            </span>
            {p.publicada_web === 'si' && (
              <span className="flex items-center gap-1 rounded-full bg-leaf/40 px-3 py-1 text-xs font-bold text-deep">
                <Globe size={12} /> En la web
              </span>
            )}
            <button
              onClick={() => setEditing(true)}
              className="ml-auto rounded-full border-2 border-deep px-4 py-1.5 text-xs font-bold text-deep transition hover:bg-deep hover:text-white"
            >
              Editar
            </button>
          </div>

          <VisitasEnLaWeb id={id} publicada={p.publicada_web === 'si'} />

          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <Item k="Zona" v={p.zona || '—'} />
            <Item k="Tipo" v={TIPO_LABEL[p.tipo] ?? p.tipo} />
            <Item k="Superficie" v={`${p.m2} m²`} />
            <Item k="Habitaciones" v={String(p.habitaciones)} />
            <Item k="Baños" v={String(p.banos)} />
            <Item k="Operación" v={OPERACION_LABEL[p.operacion] ?? p.operacion} />
            <Item k="Alquiler" v={p.precio_alquiler ? `${eur(p.precio_alquiler)}/mes` : '—'} />
            <Item k="Venta" v={eur(p.precio_venta)} />
          </dl>
          {p.descripcion && <p className="mt-4 text-sm text-ink/70">{p.descripcion}</p>}

          <ContratoYDocumentos propertyId={id} />

          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-widest text-brand">
              Fotos {pending > 0 && <span className="text-ink/40">· marcando {pending}…</span>}
            </p>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
              className="flex items-center gap-2 rounded-full bg-deep px-4 py-2 text-xs font-bold text-white transition hover:bg-brand disabled:opacity-50"
            >
              <Upload size={14} /> {upload.isPending ? 'Subiendo…' : 'Subir fotos'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              hidden
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length) upload.mutate(files);
                e.target.value = '';
              }}
            />
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {p.media?.map((m) =>
              m.status === 'ready' && m.variants ? (
                <div key={m.media_id} className="group relative overflow-hidden rounded-xl">
                  <img
                    src={`${MEDIA_BASE}/${m.variants.card}`}
                    alt=""
                    className="aspect-[4/3] w-full object-cover"
                  />
                  <button
                    onClick={() => removePhoto.mutate(m.media_id)}
                    aria-label="Eliminar foto"
                    title="Eliminar foto"
                    // En pantallas táctiles no hay «pasar el ratón»: la papelera
                    // se ve siempre. Con ratón aparece al pasar por encima.
                    className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white transition focus-visible:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ) : (
                <div
                  key={m.media_id}
                  className="flex aspect-[4/3] animate-pulse items-center justify-center rounded-xl bg-soft text-xs text-ink/40"
                >
                  Aplicando marca de agua…
                </div>
              ),
            )}
            {!p.media?.length && (
              <p className="col-span-full py-6 text-center text-sm text-ink/40">
                Sin fotos aún. Se marcan automáticamente al subirlas.
              </p>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}

/** Lo que se ha visto su ficha en la web: lo que se le puede contar al propietario. */
function VisitasEnLaWeb({ id, publicada }: { id: string; publicada: boolean }) {
  const { data } = useVisitasVivienda(id);
  if (!data || (!publicada && data.vistas === 0)) return null;
  const [y, m, d] = data.inicio.split('-').map(Number);
  const hace30 = Date.now() - 30 * 86400000;
  const periodo = new Date(y, m - 1, d).getTime() > hace30
    ? `desde el ${new Date(y, m - 1, d).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}`
    : 'en los últimos 30 días';
  const clics = Object.entries(data.acciones).filter(([, n]) => n);
  return (
    <p className="mt-3 flex flex-wrap items-center gap-x-1.5 text-sm text-ink/65">
      <TrendingUp size={14} className="text-brand" />
      <span>
        En la web, {periodo}: <b className="text-deep">{data.vistas} {data.vistas === 1 ? 'visita' : 'visitas'}</b>
        {data.tiempo_medio_s ? ` · ${tiempo(data.tiempo_medio_s)} de media` : ''}
        {clics.map(([k, n]) => ` · ${n} «${NOMBRE_ACCION[k as Accion]}»`).join('')}
      </span>
    </p>
  );
}

function Item({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-[0.65rem] font-bold uppercase tracking-wider text-ink/40">{k}</dt>
      <dd className="font-semibold text-deep">{v}</dd>
    </div>
  );
}

function Modal({
  title,
  wide,
  onClose,
  children,
}: {
  title: string;
  wide?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-deep/40 p-0 sm:items-center sm:p-6" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          'max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl sm:p-7',
          wide ? 'sm:max-w-2xl' : 'sm:max-w-xl',
        )}
      >
        <div className="mb-5 flex items-center justify-between gap-4">
          <h2 className="truncate text-lg font-extrabold text-deep">{title}</h2>
          <button onClick={onClose} aria-label="Cerrar" className="rounded-full p-1.5 text-ink/50 hover:bg-soft">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
